import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import type {
  ListUsersResponse,
  ListUsersRow,
  SortField,
  SortDirection,
} from '@rb/shared/admin-users.types';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type { AdminQueryGetInput } from '@rb/shared/admin-query.types';
import { adminQueryCount, adminQueryGet, getUserDetail, unbanUser } from '@/client/admin.client';

type Mode = 'none' | 'sort' | 'segment';

const SORT_FIELDS: SortField[] = [
  '__id',
  'createdAt',
  'lastClearedStageId',
  'totalStarCount',
  'suspiciousActivityCount',
];

type SegmentPreset = {
  key: string;
  label: string;
  filterField: string;
  /** boolean/number/string 고정값 — valueInput 이 있으면 무시. */
  fixedValue?: number | boolean | string;
  /** 사용자 입력이 필요한 경우 입력 종류. */
  valueInput?: 'number' | 'account';
  orderField: string;
  direction: SortDirection;
};

const SEGMENT_PRESETS: SegmentPreset[] = [
  {
    key: 'banned_recent',
    label: '밴 유저 · 밴일시↓',
    filterField: 'banned',
    fixedValue: true,
    orderField: 'bannedAt',
    direction: 'desc',
  },
  {
    key: 'banned_risk',
    label: '밴 유저 · 위험도↓',
    filterField: 'banned',
    fixedValue: true,
    orderField: 'suspiciousActivityCount',
    direction: 'desc',
  },
  {
    key: 'bannedby',
    label: '밴 출처별 · 밴일시↓',
    filterField: 'bannedBy',
    valueInput: 'account',
    orderField: 'bannedAt',
    direction: 'desc',
  },
  {
    key: 'wallet_signup',
    label: '지갑유저 · 가입↓',
    filterField: 'isGuest',
    fixedValue: false,
    orderField: 'createdAt',
    direction: 'desc',
  },
  {
    key: 'guest_signup',
    label: '게스트 · 가입↓',
    filterField: 'isGuest',
    fixedValue: true,
    orderField: 'createdAt',
    direction: 'desc',
  },
  {
    key: 'wallet_stage',
    label: '지갑유저 · 스테이지↓',
    filterField: 'isGuest',
    fixedValue: false,
    orderField: 'lastClearedStageId',
    direction: 'desc',
  },
  {
    key: 'wallet_stars',
    label: '지갑유저 · 별점↓',
    filterField: 'isGuest',
    fixedValue: false,
    orderField: 'totalStarCount',
    direction: 'desc',
  },
  {
    key: 'stage_cohort',
    label: '스테이지=N 코호트 · 가입↓',
    filterField: 'lastClearedStageId',
    valueInput: 'number',
    orderField: 'createdAt',
    direction: 'desc',
  },
  {
    key: 'petslot_cohort',
    label: '펫슬롯=N · 가입↓',
    filterField: 'petSlotCount',
    valueInput: 'number',
    orderField: 'createdAt',
    direction: 'desc',
  },
  {
    key: 'adremoval',
    label: '광고제거 구매자 · 가입↓',
    filterField: 'adRemovalPurchased',
    fixedValue: true,
    orderField: 'createdAt',
    direction: 'desc',
  },
  {
    key: 'invitedby',
    label: '추천인별 피추천 · 가입↓',
    filterField: 'invitedBy',
    valueInput: 'account',
    orderField: 'createdAt',
    direction: 'desc',
  },
];

interface UserListBrowserProps {
  reloadKey?: number;
  selectedRecipients?: Set<string>;
  onToggleRecipient?: (account: string) => void;
}

interface DetailState {
  state: Record<string, unknown> | null;
  assets: Record<string, number>;
}

function formatAccount(account: string | null | undefined): string {
  if (!account) return '(unknown)';
  return account;
}

export function UserListBrowser({
  reloadKey = 0,
  selectedRecipients,
  onToggleRecipient,
}: UserListBrowserProps) {
  const [mode, setMode] = useState<Mode>('none');
  const [sortField, setSortField] = useState<SortField>('__id');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [segmentKey, setSegmentKey] = useState<string>(SEGMENT_PRESETS[0].key);
  const [segmentValue, setSegmentValue] = useState<string>('');
  const [limit, setLimit] = useState<string>('20');

  const [data, setData] = useState<ListUsersResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [cursorStack, setCursorStack] = useState<Array<string | null>>([null]);
  const [pageIndex, setPageIndex] = useState(0);

  const [expandedAccount, setExpandedAccount] = useState<string | null>(null);
  const [detail, setDetail] = useState<DetailState | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const expandedAccountRef = useRef<string | null>(null);

  const [unbanning, setUnbanning] = useState<string | null>(null);

  useEffect(() => {
    expandedAccountRef.current = expandedAccount;
  }, [expandedAccount]);

  const fetchPage = useCallback(
    async (cursor: string | null) => {
      setLoading(true);
      setError(null);
      try {
        const n = Number(limit);
        const lim = Number.isFinite(n) && n > 0 ? n : 20;
        const input: AdminQueryGetInput = { collection: 'users', limit: lim };
        if (cursor) input.cursor = cursor;
        if (mode === 'sort') {
          input.orderBy = { field: sortField, direction: sortDirection };
        } else if (mode === 'segment') {
          const preset = SEGMENT_PRESETS.find((p) => p.key === segmentKey) ?? SEGMENT_PRESETS[0];
          let value: number | boolean | string;
          if (preset.valueInput === 'number') value = Number(segmentValue) || 0;
          else if (preset.valueInput === 'account') value = segmentValue.trim();
          else value = preset.fixedValue as number | boolean | string;
          input.filters = [{ field: preset.filterField, operator: '==', value }];
          input.orderBy = { field: preset.orderField, direction: preset.direction };
        }
        const [page, totalCount] = await Promise.all([
          adminQueryGet(input),
          adminQueryCount({ collection: 'users', filters: input.filters }),
        ]);
        const result: ListUsersResponse = {
          users: page.records.map((r) => {
            const { __id, ...rest } = r;
            return { account: __id, state: rest } satisfies ListUsersRow;
          }),
          hasMore: page.hasMore,
          nextCursor: page.nextCursor,
          totalCount,
        };
        setData(result);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [limit, mode, sortField, sortDirection, segmentKey, segmentValue]
  );

  // Initial load + reload on parent trigger / mode change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reloadKey is intentionally used as a trigger from parent to force re-fetch
  useEffect(() => {
    setCursorStack([null]);
    setPageIndex(0);
    setExpandedAccount(null);
    setDetail(null);
    setDetailError(null);
    void fetchPage(null);
  }, [fetchPage, reloadKey]);

  const handleNext = () => {
    if (!data?.hasMore || !data.nextCursor) return;
    const newStack = [...cursorStack, data.nextCursor];
    setCursorStack(newStack);
    setPageIndex(newStack.length - 1);
    void fetchPage(data.nextCursor);
  };

  const handlePrev = () => {
    if (pageIndex === 0) return;
    const newStack = cursorStack.slice(0, -1);
    setCursorStack(newStack);
    setPageIndex(newStack.length - 1);
    void fetchPage(newStack[newStack.length - 1]);
  };

  const handleReset = () => {
    setMode('none');
    setSortField('__id');
    setSortDirection('asc');
    setSegmentKey(SEGMENT_PRESETS[0].key);
    setSegmentValue('');
    setLimit('20');
  };

  const handleRowClick = async (account: string) => {
    if (expandedAccount === account) {
      setExpandedAccount(null);
      setDetail(null);
      setDetailError(null);
      return;
    }
    setExpandedAccount(account);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const d = await getUserDetail(account);
      // Stale-response guard: another row might have been clicked in flight.
      if (expandedAccountRef.current !== account) return;
      setDetail(d);
    } catch (e) {
      if (expandedAccountRef.current !== account) return;
      setDetailError(e instanceof Error ? e.message : String(e));
    } finally {
      if (expandedAccountRef.current === account) setDetailLoading(false);
    }
  };

  async function handleUnban(account: string) {
    if (!confirm(`${account} 밴을 해제할까요?`)) return;
    setUnbanning(account);
    try {
      await unbanUser(account);
      void fetchPage(cursorStack[pageIndex]);
    } catch (e) {
      alert(`Unban 실패: ${(e as Error).message}`);
    } finally {
      setUnbanning(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Users browser</CardTitle>
        <CardDescription>
          Paginated list of users in the V8 `users` collection. 정렬 또는 세그먼트(인덱스 기반) —
          전부 cursor 페이지네이션. Click a row for full state + assets.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {/* Mode toggle */}
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-muted-foreground">Mode</Label>
            <div className="flex gap-1">
              {(['none', 'sort', 'segment'] as Mode[]).map((m) => (
                <Button
                  key={m}
                  type="button"
                  variant={mode === m ? 'default' : 'secondary'}
                  size="sm"
                  onClick={() => setMode(m)}
                  disabled={loading}
                  className={cn(mode === m && 'pointer-events-none')}
                >
                  {m}
                </Button>
              ))}
            </div>
          </div>

          {mode === 'sort' && (
            <>
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-muted-foreground">Sort field</Label>
                <Select
                  value={sortField}
                  onValueChange={(v) => setSortField(v as SortField)}
                  disabled={loading}
                >
                  <SelectTrigger className="w-44">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SORT_FIELDS.map((f) => (
                      <SelectItem key={f} value={f}>
                        {f}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-muted-foreground">Direction</Label>
                <Select
                  value={sortDirection}
                  onValueChange={(v) => setSortDirection(v as SortDirection)}
                  disabled={loading}
                >
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asc">asc</SelectItem>
                    <SelectItem value="desc">desc</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {mode === 'segment' && (
            <>
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-muted-foreground">세그먼트 (인덱스 기반)</Label>
                <Select value={segmentKey} onValueChange={setSegmentKey} disabled={loading}>
                  <SelectTrigger className="w-60">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEGMENT_PRESETS.map((p) => (
                      <SelectItem key={p.key} value={p.key}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(() => {
                const preset = SEGMENT_PRESETS.find((p) => p.key === segmentKey);
                if (!preset?.valueInput) return null;
                return (
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs text-muted-foreground">
                      {preset.valueInput === 'account' ? '계정 (==)' : 'N (==)'}
                    </Label>
                    <Input
                      type={preset.valueInput === 'account' ? 'text' : 'number'}
                      value={segmentValue}
                      onChange={(e) => setSegmentValue(e.target.value)}
                      disabled={loading}
                      className="w-44"
                    />
                  </div>
                );
              })()}
            </>
          )}

          <div className="flex flex-col gap-1">
            <Label className="text-xs text-muted-foreground">Limit</Label>
            <Input
              type="number"
              min={1}
              max={100}
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              disabled={loading}
              className="w-24"
            />
          </div>

          <Button
            type="button"
            onClick={() => void fetchPage(cursorStack[pageIndex])}
            disabled={loading}
          >
            {loading ? 'Loading…' : 'Reload'}
          </Button>
          <Button type="button" variant="ghost" onClick={handleReset} disabled={loading}>
            Reset
          </Button>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription className="whitespace-pre-wrap break-words">{error}</AlertDescription>
          </Alert>
        )}

        {/* Table */}
        {!error && (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead className="text-right">petSlotCount</TableHead>
                  <TableHead className="text-right">pets</TableHead>
                  <TableHead className="text-right">quests</TableHead>
                  <TableHead className="text-right">stage</TableHead>
                  <TableHead className="text-right">Stars</TableHead>
                  <TableHead className="text-right">의심</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.users.map((u: ListUsersRow) => {
                  const petsLen = Array.isArray(u.state.pets)
                    ? (u.state.pets as unknown[]).length
                    : 0;
                  const questsLen = Array.isArray(u.state.quests)
                    ? (u.state.quests as unknown[]).length
                    : 0;
                  const lastCleared = Number(u.state.lastClearedStageId) || 0;
                  const slot = Number(u.state.petSlotCount) || 0;
                  const banned = Boolean(u.state.banned);
                  const expanded = expandedAccount === u.account;
                  return (
                    <Fragment key={u.account}>
                      <TableRow
                        className="cursor-pointer"
                        onClick={() => void handleRowClick(u.account)}
                      >
                        <TableCell className="w-8" onClick={(e) => e.stopPropagation()}>
                          {onToggleRecipient && (
                            <input
                              type="checkbox"
                              aria-label={`select ${u.account}`}
                              checked={selectedRecipients?.has(u.account) ?? false}
                              onChange={() => onToggleRecipient(u.account)}
                            />
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs break-all" title={u.account}>
                          <span className="inline-flex flex-wrap items-center gap-1.5">
                            {formatAccount(u.account)}
                            {banned && (
                              <Badge variant="destructive" className="px-1 py-0 text-[10px]">
                                BANNED
                              </Badge>
                            )}
                            {banned && (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={unbanning === u.account}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void handleUnban(u.account);
                                }}
                              >
                                {unbanning === u.account ? '...' : 'Unban'}
                              </Button>
                            )}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">{slot}</TableCell>
                        <TableCell className="text-right">{petsLen}</TableCell>
                        <TableCell className="text-right">{questsLen}</TableCell>
                        <TableCell className="text-right">{lastCleared}</TableCell>
                        <TableCell className="text-right">
                          {(u.state as { totalStarCount?: number }).totalStarCount ?? '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          {(u.state as { suspiciousActivityCount?: number })
                            .suspiciousActivityCount ?? '-'}
                        </TableCell>
                        <TableCell className="text-right">{expanded ? '▼' : '▶'}</TableCell>
                      </TableRow>
                      {expanded && (
                        <TableRow>
                          <TableCell colSpan={9} className="bg-muted/30">
                            {detailLoading ? (
                              <p className="text-sm text-muted-foreground">Loading detail…</p>
                            ) : detailError ? (
                              <Alert variant="destructive">
                                <AlertDescription className="whitespace-pre-wrap break-words">
                                  {detailError}
                                </AlertDescription>
                              </Alert>
                            ) : detail ? (
                              <div className="flex flex-col gap-3 py-2">
                                <div className="flex flex-col gap-1">
                                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                    Assets
                                  </h4>
                                  <div className="flex flex-wrap gap-2">
                                    {Object.entries(detail.assets).map(([k, v]) => (
                                      <Badge key={k} variant="secondary" className="font-normal">
                                        {k}:&nbsp;<span className="font-bold">{v}</span>
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                                <Separator />
                                <div className="flex flex-col gap-1">
                                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                    State
                                  </h4>
                                  <ScrollArea className="h-[300px] rounded-md border bg-muted/40">
                                    <pre className="p-3 text-xs">
                                      {detail.state
                                        ? JSON.stringify(detail.state, null, 2)
                                        : 'null'}
                                    </pre>
                                  </ScrollArea>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground">No data</p>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
                {data && data.users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-sm text-muted-foreground">
                      No users
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                Page {pageIndex + 1} · {data?.users.length ?? 0} users on this page · total{' '}
                {data?.totalCount ?? 0}
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={handlePrev}
                  disabled={loading || pageIndex === 0}
                >
                  Prev
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleNext}
                  disabled={loading || !data?.hasMore}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default UserListBrowser;
